import app from 'flarum/forum/app';
import Component from 'flarum/common/Component';
import extractText from 'flarum/common/utils/extractText';
import collectDescendants from '../utils/tagTree';
const DEBOUNCE_MS = 300;
const MIN_AUTHOR_QUERY_LENGTH = 3;

// Mirrors champis-species' own SpeciesAutocompleteResult (js/src/common/
// speciesTypes.ts) - duplicated rather than imported, same as every other
// cross-extension shape in this codebase, since there's no build-time
// dependency on champis-species to import through. Only `id` is actually
// read here; kept to just that rather than the widget's full result shape
// so this doesn't silently drift from what this file really uses.

// A static `import ... from 'ext:champis-species/...'` captures whatever
// flarum.reg holds at *this module's own* evaluation time - which, despite
// champis-species loading first in extensions_enabled, isn't guaranteed to
// be after champis-species has finished registering its own exports (both
// extensions' JS ends up concatenated into one combined forum.js, and nothing
// orders their top-level module evaluation to match). Confirmed directly:
// that binding was undefined here, throwing "the selector must be either a
// string or a component" the moment Mithril tried to render it. Resolved
// lazily instead, at actual render time (well after boot), the same way
// core's own extend() defers a string-path target via flarum.reg.onLoad
// internally (see addRegistrationTerms.js for that other case).
function getSpeciesSearchWidget() {
  return window.flarum.reg.get('champis-species', 'forum/components/SpeciesSearchWidget');
}

// Sets or removes a single /api/discussions filter key on the shared
// DiscussionListState IndexPage's own list renders from (app.discussions -
// the same object core's sort dropdown and flarum-tags' own tag-page
// filtering already mutate this way, so the list re-renders normally with
// no extra wiring here). Deletes the key outright when cleared rather than
// sending an empty value: an empty `species`/`tag` wouldn't resolve to "no
// constraint" the way an empty `title`/`authorName` LIKE would - it'd
// resolve to "matches nothing", the opposite of clearing the filter.
function setFilter(key, value) {
  const filter = {
    ...app.discussions.getParams().filter
  };
  if (value === undefined || value === null || value === '') {
    delete filter[key];
  } else {
    filter[key] = value;
  }
  app.discussions.refreshParams({
    ...app.discussions.getParams(),
    filter
  }, 1);
}

/**
 * Filter row for the homepage's "Discussions du forum" list, shown/hidden
 * by the funnel icon in the header above (see redesignIndexPage.js) -
 * matching champis.net's own funnel filter widget there, plus a category
 * filter it doesn't have. Always mounted, starting hidden via the
 * ChampisDiscussionFilters--hidden class rather than a conditional `null`
 * return: the funnel icon toggles that class with plain DOM manipulation
 * (see redesignIndexPage.js's own comment for why - IndexPage's
 * SubtreeRetainer never re-invokes contentItems() for a change that
 * doesn't touch app.discussions), so there needs to be a real element
 * already in the DOM for it to toggle.
 *
 * Fields:
 *  - title / author: free-text, partial match (champis-forum's own
 *    TitleFilter/AuthorNameFilter - core's `q` only matches whole words and
 *    searches post content too, and `author` needs an exact username).
 *    Author also suggests matching usernames as you type (AuthorAutocompleteResource,
 *    same "type a few letters" convenience as mentioning someone in a
 *    post) - picking one applies the filter immediately rather than
 *    waiting for the debounce.
 *  - species: reuses champis-species' own SpeciesSearchWidget in onselect
 *    mode - filter[species] (SpeciesFilter) already resolves synonyms, so
 *    picking "Amanita muscaria" surfaces discussions tagged under any of
 *    its synonyms too, same as the species page's own discussion list.
 *  - category: a plain <select> over the same tag tree addForumsMenu.js's
 *    own header dropdown uses (core's `filter[tag]`, nothing custom
 *    needed).
 *
 * Every keyed sibling group below is built as one combined array literal
 * rather than separate adjacent JSX expressions (`<Foo key=.../>{arr}`) -
 * confirmed directly that Mithril's keyed-diffing rejects a bare keyed
 * vnode and a keyed array sitting as two separate child *positions*, even
 * statically with nothing conditional or async involved ("vnodes must
 * either all have keys or none have keys... instead of a hole"). One flat
 * array per keyed group sidesteps it.
 */
export default class DiscussionFilters extends Component {
  title = '';
  authorName = '';
  authorResults = [];
  authorOpen = false;
  selectedSpecies = null;
  // Bumped on clearSpecies() to force SpeciesSearchWidget to remount
  // (via its `key` below) - that's the only way to reset its own
  // internal query text back to empty, since it doesn't accept a
  // controlled value prop.
  speciesKey = 0;
  tagsLoaded = false;
  titleDebounce = null;
  authorDebounce = null;
  // Set by the <select>'s oncreate below.
  categorySelectEl = null;
  oninit(vnode) {
    super.oninit(vnode);
    const filter = app.discussions.getParams().filter || {};
    this.title = filter.title || '';
    this.authorName = filter.authorName || '';
    this.authorResults = [];
    this.authorOpen = false;
    this.selectedSpecies = null;
    this.speciesKey = 0;
    this.tagsLoaded = false;
    this.titleDebounce = null;
    this.authorDebounce = null;
    this.categorySelectEl = null;

    // m.redraw() alone doesn't reach this component for an update that
    // doesn't touch app.discussions - same IndexPage SubtreeRetainer as
    // the funnel toggle (see redesignIndexPage.js), just tripped by an
    // async promise instead of a click this time. Populating directly
    // off the stored DOM ref here, rather than trusting onupdate to fire
    // on the <select> once tagsLoaded flips, sidesteps it the same way.
    app.tagList.load(['parent']).then(() => {
      this.tagsLoaded = true;
      if (this.categorySelectEl) {
        this.populateCategoryOptions(this.categorySelectEl);
      }
    });
  }
  view() {
    const t = function (key, params) {
      if (params === void 0) {
        params = {};
      }
      return app.translator.trans(`champis-forum.forum.index.${key}`, params);
    };
    const SpeciesSearchWidget = getSpeciesSearchWidget();
    const speciesChildren = [SpeciesSearchWidget ? m(SpeciesSearchWidget, {
      key: this.speciesKey,
      placeholder: t('filter_species_placeholder'),
      onselect: species => {
        this.selectedSpecies = species;
        setFilter('species', species.id);
      }
    }) : m("span", {
      key: "species-placeholder"
    })];
    if (this.selectedSpecies) {
      speciesChildren.push(m("a", {
        key: "clear",
        className: "ChampisDiscussionFilters-clear",
        "aria-label": extractText(t('filter_clear_accessible_label')),
        title: extractText(t('filter_clear_accessible_label')),
        onclick: () => this.clearSpecies()
      }, m("i", {
        className: "icon fas fa-times",
        "aria-hidden": "true"
      })));
    }
    return m("div", {
      className: "ChampisDiscussionFilters ChampisDiscussionFilters--hidden"
    }, m("div", {
      className: "ChampisDiscussionFilters-field ChampisDiscussionFilters-species"
    }, speciesChildren), m("div", {
      className: "ChampisDiscussionFilters-field"
    }, m("input", {
      className: "FormControl",
      type: "text",
      placeholder: extractText(t('filter_title_placeholder')),
      "aria-label": extractText(t('filter_title_placeholder')),
      value: this.title,
      oninput: e => this.onTitleInput(e.target.value)
    })), m("div", {
      className: "ChampisDiscussionFilters-field ChampisDiscussionFilters-author"
    }, m("input", {
      className: "FormControl",
      type: "text",
      placeholder: extractText(t('filter_author_placeholder')),
      "aria-label": extractText(t('filter_author_placeholder')),
      value: this.authorName,
      oninput: e => this.onAuthorInput(e.target.value),
      onfocus: () => {
        this.authorOpen = this.authorResults.length > 0;
      },
      onblur: () => {
        // Closes the list when the field loses focus to anything but the
        // list itself - see SpeciesSearchWidget's onblur, same pattern.
        setTimeout(() => {
          this.authorOpen = false;
          m.redraw();
        }, 150);
      }
    }), this.authorOpen && this.authorResults.length > 0 && m("ul", {
      className: "ChampisDiscussionFilters-authorResults",
      onmousedown: e => e.preventDefault()
    }, this.authorResults.map(username => m("li", {
      key: username,
      onclick: () => this.selectAuthor(username)
    }, username)))), m("div", {
      className: "ChampisDiscussionFilters-field"
    }, m("select", {
      className: "FormControl",
      "aria-label": extractText(t('filter_category_accessible_label')),
      onchange: e => setFilter('tag', e.target.value),
      oncreate: vnode => {
        this.categorySelectEl = vnode.dom;
        this.populateCategoryOptions(vnode.dom);
      }
    }, m("option", {
      value: ""
    }, t('filter_category_all')))));
  }

  // Populates the <select> with <optgroup>/<option> elements via plain DOM
  // APIs instead of JSX/vnode children - Mithril's keyed-diffing rejects a
  // bare keyed vnode and a keyed array as separate sibling child
  // *positions* even in the simplest static case (confirmed directly,
  // "vnodes must either all have keys or none have keys... instead of a
  // hole"), which the "all" option next to the tag list otherwise runs
  // into the moment the tag list goes from empty (not loaded yet, see
  // oninit) to populated. Guarded by a dataset flag so this only runs
  // once - re-running on every redraw would duplicate the options it
  // already appended last time, since nothing here is diffed against a
  // previous vnode tree the way JSX children normally would be.
  populateCategoryOptions(selectEl) {
    if (!this.tagsLoaded || selectEl.dataset.populated) return;
    selectEl.dataset.populated = 'true';
    const tags = app.store.all('tags');
    const fragment = document.createDocumentFragment();
    tags.filter(tag => !tag.parent()).sort((a, b) => (a.position() ?? 0) - (b.position() ?? 0)).forEach(root => {
      // Same any-depth traversal as addForumsMenu.js - this hierarchy
      // sometimes goes 3 levels (e.g. "Rédactions" -> "Liens" ->
      // "Ascomycètes").
      const descendants = collectDescendants(tags, root, (a, b) => a.champisOrder() - b.champisOrder());
      if (!descendants.length) return;
      const optgroup = document.createElement('optgroup');
      optgroup.label = root.name();
      descendants.forEach(_ref => {
        let {
          tag,
          depth
        } = _ref;
        const option = document.createElement('option');
        option.value = tag.slug();
        option.textContent = '  '.repeat(depth) + tag.name();
        optgroup.appendChild(option);
      });
      fragment.appendChild(optgroup);
    });
    selectEl.appendChild(fragment);
  }
  onTitleInput(value) {
    this.title = value;
    if (this.titleDebounce) clearTimeout(this.titleDebounce);
    this.titleDebounce = setTimeout(() => setFilter('title', value.trim()), DEBOUNCE_MS);
  }
  onAuthorInput(value) {
    this.authorName = value;
    if (this.authorDebounce) clearTimeout(this.authorDebounce);
    const trimmed = value.trim();
    if (trimmed.length < MIN_AUTHOR_QUERY_LENGTH) {
      this.authorResults = [];
      this.authorOpen = false;
    }
    this.authorDebounce = setTimeout(() => {
      setFilter('authorName', trimmed);
      if (trimmed.length >= MIN_AUTHOR_QUERY_LENGTH) {
        this.searchAuthors(trimmed);
      }
    }, DEBOUNCE_MS);
  }
  searchAuthors(query) {
    app.request({
      method: 'GET',
      url: app.forum.attribute('apiUrl') + '/author-autocomplete',
      params: {
        filter: {
          query
        }
      }
    }).then(response => {
      // Stale response for a query the user has since changed/cleared.
      if (query !== this.authorName.trim()) return;
      this.authorResults = (response.data || []).map(item => item.attributes.username);
      this.authorOpen = true;
      m.redraw();
    }).catch(() => {
      this.authorResults = [];
      m.redraw();
    });
  }
  selectAuthor(username) {
    if (this.authorDebounce) clearTimeout(this.authorDebounce);
    this.authorName = username;
    this.authorOpen = false;
    setFilter('authorName', username);
  }
  clearSpecies() {
    this.selectedSpecies = null;
    this.speciesKey += 1;
    setFilter('species', undefined);
  }
}
flarum.reg.add('champis-forum', 'forum/components/DiscussionFilters', DiscussionFilters);